import { Injectable, Inject } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { ToastrService } from 'ngx-toastr';

import { Pedido } from '../../models/pedidos/pedido';
import { PedidoBonificacaoRetorno } from '../../models/pedidos/pedido-bonificacao-retorno';
import { PedidoItens } from '../../models/pedidos/pedido-itens';
import { PedidoPagamento } from '../../models/pedidos/pedido-pagamento';
import { PedidoPendenciaRetorno } from '../../models/pedidos/pedido-pendencia-retorno';
import { PedidoSintetico } from '../../models/pedidos/pedido-sintetico';
import { ResumoFinanceiro } from '../../models/financeiro/resumo-financeiro';
import { CotaPromocaoResultado } from '../../models/produtos/cota-promocao-resultado';

export interface PedidosPagedRequest {
  id?: string;
  atencao?: boolean;
  page: number;
  pageSize: number;
  sort?: string;
  direction?: string;
  dataInicio?: string;
  dataFim?: string;
  cliente?: string;
  nroPedido?: number;
  status?: string;
  vendedor?: string;
  filtrarB2B?: boolean;
  filtrarCancelado?: boolean;
  numeroNf?: string;
  dtFaturamentoInicio?: string;
  dtFaturamentoFim?: string;
}

export interface PagedResult<T> {
  items: T[];
  /** null quando o backend usa modo hasMore (sem COUNT) */
  total?: number;
  hasMore?: boolean;
  page: number;
  pageSize: number;
}

export interface PedidoStatsResult {
  qtdePedidos: number;
  qtdePositivacao?: number;
  valorVendas: number;
  litragemTotal: number;
  statusStats: { status_pedido: string; valor: number; litragem: number; quantidade: number }[];
  topClientes?: { client_id: number; name: string; total: number; litragem: number; qtdePedidos: number }[];
}

export interface PedidoStatsUnificadoRequest {
  id?: number;
  tela: 'ATENCAO' | 'ENVIADOS' | 'NAO_ENVIADOS' | 'PENDENCIAS';
  dataInicio?: string;
  dataFim?: string;
  vendedor?: string;
  cliente?: string;
  nroPedido?: number;
  status?: string;
  numeroNf?: string;
  dtFaturamentoInicio?: string;
  dtFaturamentoFim?: string;
}

@Injectable({
  providedIn: 'root'
})

export class PedidoService {
  private baseUrl: string;
  private headers = new HttpHeaders();

  constructor(
    private http: HttpClient,
    @Inject("BASE_URL") baseUrl: string,
    private toastr: ToastrService
  ) {
    this.baseUrl = baseUrl;
  }

  public add(pedido: Pedido): Observable<boolean> {
    this.headers.set("Content-Type", "application/json");

    let data = {
      "pedido": pedido
    };

    return this.http.post<boolean>(this.baseUrl + "api/Pedido/Add", data, { headers: this.headers }).pipe(
      catchError(() => {
        this.toastr.error("", "Houve um erro, contate o suporte...");

        return of(false);
      })
    );
  }

  public getByOrder(order_id_app: number, seller_id: number): Observable<Pedido> {
    this.headers.set("Content-Type", "application/json");

    let data = {
      "order_id_app": order_id_app,
      "seller_id": seller_id
    };

    return this.http.post<Pedido>(this.baseUrl + "api/Pedido/GetByOrder", data, { headers: this.headers }).pipe(
      catchError(() => {
        this.toastr.error("", "Houve um erro, contate o suporte...");

        return of(null);
      })
    );
  }

  public getProximoNumeroPedidoBySeller(): Observable<number> {
    this.headers.set("Content-Type", "application/json");

    let is_master: boolean = (localStorage.getItem("is_master") == "true");
    let is_manager: boolean = (localStorage.getItem("is_manager") == "true");
    let is_master_seller: boolean = (localStorage.getItem("is_master_seller") == "true");
    let is_diretor: boolean = (localStorage.getItem("is_diretor") == "true");

    let seller_id: number = 125;

    if (is_master || is_manager || is_master_seller || is_diretor) {
      seller_id = parseInt(localStorage.getItem("id"));
    }

    let data = {
      "seller_id": seller_id
    };

    return this.http.post<number>(this.baseUrl + "api/Pedido/GetProximoNumeroPedidoBySeller", data, { headers: this.headers }).pipe(
      catchError(() => {
        this.toastr.error("", "Houve um erro, contate o suporte...");

        return of(0);
      })
    );
  }

  public update(pedido: Pedido): Observable<boolean> {
    this.headers.set("Content-Type", "application/json");

    let data = {
      "pedido": pedido
    };

    return this.http.post<boolean>(this.baseUrl + "api/Pedido/Update", data, { headers: this.headers }).pipe(
      catchError(() => {
        this.toastr.error("", "Houve um erro, contate o suporte...");

        return of(false);
      })
    );
  }

  public updateDataIntegracaoByOrderIdApp(order_id_app: number, seller_id: number): Observable<boolean> {
    this.headers.set("Content-Type", "application/json");

    let data = {
      "order_id_app": order_id_app,
      "seller_id": seller_id
    };

    return this.http.post<boolean>(this.baseUrl + "api/Pedido/UpdateDataIntegracaoByOrderIdApp", data, { headers: this.headers }).pipe(
      catchError(() => {
        this.toastr.error("", "Houve um erro, contate o suporte...");

        return of(false);
      })
    );
  }

  /* Pagamento */

  public getPedidoPagamentoById(id: number): Observable<PedidoPagamento> {
    this.headers.set("Content-Type", "application/json");

    let data = {
      "guidclientenapis": localStorage.getItem("guidclientenapis"),
      "id": id
    };

    return this.http.post<PedidoPagamento>(this.baseUrl + "api/Pedido/GetPedidoPagamentoById", data, { headers: this.headers }).pipe(
      catchError(() => {
        this.toastr.error("", "Houve um erro, contate o suporte...");

        return of(null);
      })
    );
  }

  /* Sintético */

  public getByCliente(client_id_app: number): Observable<PedidoSintetico[]> {
    this.headers.set("Content-Type", "application/json");

    let data = {
      "client_id_app": client_id_app
    };

    return this.http.post<PedidoSintetico[]>(this.baseUrl + "api/Pedido/GetByCliente", data, { headers: this.headers }).pipe(
      catchError(() => {
        this.toastr.error("", "Houve um erro, contate o suporte...");

        return of([]);
      })
    );
  }

  public getByUsuario(atencao?: boolean): Observable<PedidoSintetico[]> {
    this.headers.set("Content-Type", "application/json");

    atencao = atencao == null ? false : atencao;

    let data = {
      "id": localStorage.getItem("id"),
      "atencao": atencao
    };

    return this.http.post<PedidoSintetico[]>(this.baseUrl + "api/Pedido/GetByUsuario", data, { headers: this.headers }).pipe(
      catchError(() => {
        this.toastr.error("", "Houve um erro, contate o suporte...");

        return of([]);
      })
    );
  }

  public getStatusPedidoByOrder(order_id: number, seller_id: number): Observable<string> {
    this.headers.set("Content-Type", "application/json");

    let data = {
      "order_id": order_id,
      "seller_id": seller_id
    };

    return this.http.post<string>(this.baseUrl + "api/Pedido/GetStatusPedidoByOrder", data, { headers: this.headers }).pipe(
      catchError(() => {
        this.toastr.error("", "Houve um erro, contate o suporte...");

        return of("");
      })
    );
  }

  /* Ações */

  public aprovar(id: number, seller_id: number): Observable<boolean> {
    this.headers.set("Content-Type", "application/json");

    let data = {
      "guidclientenapis": localStorage.getItem("guidclientenapis"),
      "id": id,
      "seller_id_logado": localStorage.getItem("id"),
      "seller_id_pedido": seller_id
    };

    return this.http.post<boolean>(this.baseUrl + "api/Pedido/Aprovar", data, { headers: this.headers }).pipe(
      catchError(() => {
        this.toastr.error("", "Houve um erro, contate o suporte...");

        return of(false);
      })
    );
  }

  public calcularSaldo(order_id_app: number, seller_id_client: number, seller_id_pedido: number): Observable<boolean> {
    this.headers.set("Content-Type", "application/json");

    let data = {
      "order_id_app": order_id_app,
      "seller_id_client": seller_id_client,
      "seller_id_pedido": seller_id_pedido
    };

    return this.http.post<boolean>(this.baseUrl + "api/Pedido/CalcularSaldo", data, { headers: this.headers }).pipe(
      catchError(() => {
        this.toastr.error("", "Houve um erro, contate o suporte...");

        return of(false);
      })
    );
  }

  public enviar(order_id_app: number, seller_id: number, percentual_desconto: number): Observable<PedidoBonificacaoRetorno> {
    this.headers.set("Content-Type", "application/json");

    let data = {
      "guidclientenapis": localStorage.getItem("guidclientenapis"),
      "order_id_app": order_id_app,
      "seller_id_logado": localStorage.getItem("id"),
      "seller_id_pedido": seller_id,
      "percentual_desconto": percentual_desconto
    };

    return this.http.post<PedidoBonificacaoRetorno>(this.baseUrl + "api/Pedido/Enviar", data, { headers: this.headers }).pipe(
      catchError((err) => {
        // O backend devolve 400 { mensagem } para bloqueios de negócio (cota esgotada,
        // falta de estoque). Mostra a mensagem específica quando houver.
        const mensagem = err?.error?.mensagem;
        this.toastr.error("", mensagem ? mensagem : "Houve um erro, contate o suporte...");

        return of(null);
      })
    );
  }

  public reintegrar(id: number, seller_id: number): Observable<boolean> {
    this.headers.set("Content-Type", "application/json");

    let data = {
      "guidclientenapis": localStorage.getItem("guidclientenapis"),
      "id": id,
      "seller_id_logado": localStorage.getItem("id"),
      "seller_id_pedido": seller_id
    };

    return this.http.post<boolean>(this.baseUrl + "api/Pedido/Reintegrar", data, { headers: this.headers }).pipe(
      catchError(() => {
        this.toastr.error("", "Houve um erro, contate o suporte...");

        return of(false);
      })
    );
  }

  public reprovar(id: number, seller_id: number, motivo: string): Observable<boolean> {
    this.headers.set("Content-Type", "application/json");

    let data = {
      "id": id,
      "seller_id_logado": localStorage.getItem("id"),
      "seller_id_pedido": seller_id,
      "motivo": motivo
    };

    return this.http.post<boolean>(this.baseUrl + "api/Pedido/Reprovar", data, { headers: this.headers }).pipe(
      catchError(() => {
        this.toastr.error("", "Houve um erro, contate o suporte...");

        return of(false);
      })
    );
  }

  public calcularFrete(totalPedido: number, clientId: number): Observable<{ valorFrete: number; regraAplicavel: boolean }> {
    this.headers.set("Content-Type", "application/json");

    let data: any = {
      "guidclientenapis": localStorage.getItem("guidclientenapis"),
      "totalPedido": totalPedido,
      "client_id": clientId
    };

    return this.http.post<{ valorFrete: number; regraAplicavel: boolean }>(this.baseUrl + "api/Pedido/CalcularFrete", data, { headers: this.headers });
  }

  public retornar(id: number, seller_id: number): Observable<boolean> {
    this.headers.set("Content-Type", "application/json");

    let data = {
      "id": id,
      "seller_id_logado": localStorage.getItem("id"),
      "seller_id_pedido": seller_id
    };

    return this.http.post<boolean>(this.baseUrl + "api/Pedido/Retornar", data, { headers: this.headers }).pipe(
      catchError(() => {
        this.toastr.error("", "Houve um erro, contate o suporte...");

        return of(false);
      })
    );
  }

  public desativar(id: number, seller_id: number): Observable<boolean> {
    this.headers.set("Content-Type", "application/json");

    let data = {
      "id": id,
      "seller_id": seller_id
    };

    return this.http.post<boolean>(this.baseUrl + "api/Pedido/Desativar", data, { headers: this.headers }).pipe(
      catchError(() => {
        this.toastr.error("", "Houve um erro, contate o suporte...");

        return of(false);
      })
    );
  }

  public getResumoFinanceiroAprovacao(order_id_app: number, seller_id: number): Observable<ResumoFinanceiro> {
    this.headers.set("Content-Type", "application/json");

    let data = {
      "order_id_app": order_id_app,
      "seller_id": seller_id
    };

    return this.http.post<ResumoFinanceiro>(
      this.baseUrl + "api/Pedido/GetResumoFinanceiroAprovacao",
      data,
      { headers: this.headers }
    ).pipe(
      catchError(() => of(null))
    );
  }

  public getResumoFinanceiroPorCliente(guidClienteNapis: string, codigoErp: string, clienteNome: string): Observable<ResumoFinanceiro> {
    this.headers.set('Content-Type', 'application/json');
    const data = {
      guidclientenapis: guidClienteNapis,
      codigo_no_erp_do_cliente: codigoErp,
      cliente_nome: clienteNome
    };
    return this.http.post<ResumoFinanceiro>(
      this.baseUrl + 'api/Pedido/GetResumoFinanceiroPorCliente',
      data,
      { headers: this.headers }
    );
  }

  /* Sintético — Paginação */

  public getEnviadosPaged(req: PedidosPagedRequest): Observable<PagedResult<PedidoSintetico>> {
    this.headers.set('Content-Type', 'application/json');
    const data = { ...req, id: req.id != null ? req.id : localStorage.getItem('id') };
    return this.http.post<PagedResult<PedidoSintetico>>(this.baseUrl + 'api/Pedido/ListEnviadosPaged', data, { headers: this.headers }).pipe(
      catchError(() => {
        this.toastr.error('', 'Houve um erro, contate o suporte...');
        return of({ items: [], total: 0, hasMore: false, page: req.page, pageSize: req.pageSize });
      })
    );
  }

  public getAtencaoPaged(req: PedidosPagedRequest): Observable<PagedResult<PedidoSintetico>> {
    this.headers.set('Content-Type', 'application/json');
    const data = { ...req, id: req.id != null ? req.id : localStorage.getItem('id') };
    return this.http.post<PagedResult<PedidoSintetico>>(this.baseUrl + 'api/Pedido/ListAtencaoPaged', data, { headers: this.headers }).pipe(
      catchError(() => {
        this.toastr.error('', 'Houve um erro, contate o suporte...');
        return of({ items: [], total: 0, hasMore: false, page: req.page, pageSize: req.pageSize });
      })
    );
  }

  public getStatsEnviados(req: PedidosPagedRequest): Observable<PedidoStatsResult> {
    this.headers.set('Content-Type', 'application/json');
    const data = { ...req, id: req.id != null ? req.id : localStorage.getItem('id') };
    return this.http.post<PedidoStatsResult>(this.baseUrl + 'api/Pedido/StatsEnviados', data, { headers: this.headers }).pipe(
      catchError(() => of(null))
    );
  }

  public getStatsAtencao(req: PedidosPagedRequest): Observable<PedidoStatsResult> {
    this.headers.set('Content-Type', 'application/json');
    const data = { ...req, id: req.id != null ? req.id : localStorage.getItem('id') };
    return this.http.post<PedidoStatsResult>(this.baseUrl + 'api/Pedido/StatsAtencao', data, { headers: this.headers }).pipe(
      catchError(() => of(null))
    );
  }

  public getStatsUnificado(req: PedidoStatsUnificadoRequest): Observable<PedidoStatsResult> {
    this.headers.set('Content-Type', 'application/json');
    const data = {
      id: req.id != null ? req.id : parseInt(localStorage.getItem('id'), 10),
      tela: req.tela,
      dataInicio: req.dataInicio || null,
      dataFim: req.dataFim || null,
      vendedor: req.vendedor || null,
      cliente: req.cliente || null,
      nroPedido: req.nroPedido || null,
      status: req.status || null,
      numeroNf: req.numeroNf || null,
      dtFaturamentoInicio: req.dtFaturamentoInicio || null,
      dtFaturamentoFim: req.dtFaturamentoFim || null
    };

    return this.http.post<PedidoStatsResult>(this.baseUrl + 'api/Pedido/Stats', data, { headers: this.headers }).pipe(
      catchError(() => of(null))
    );
  }

  /* Pendência */

  public criarPedidoPendencia(order_id_origem: number, order_id_app: number, seller_id: number, itens: PedidoItens[]): Observable<PedidoPendenciaRetorno> {
    this.headers.set("Content-Type", "application/json");

    let data = {
      "order_id_origem": order_id_origem,
      "order_id_app": order_id_app,
      "seller_id": seller_id,
      "itens_pendencia": itens
    };

    return this.http.post<PedidoPendenciaRetorno>(this.baseUrl + "api/Pedido/CriarPedidoPendencia", data, { headers: this.headers }).pipe(
      catchError(() => {
        this.toastr.error("", "Houve um erro ao criar o pedido pendência...");

        return of(null);
      })
    );
  }

  public getPendenciasByOrigem(order_id_origem: number, seller_id: number): Observable<Pedido[]> {
    this.headers.set("Content-Type", "application/json");

    let data = {
      "order_id_origem": order_id_origem,
      "seller_id": seller_id
    };

    return this.http.post<Pedido[]>(this.baseUrl + "api/Pedido/GetPendenciasByOrigem", data, { headers: this.headers }).pipe(
      catchError(() => {
        this.toastr.error("", "Houve um erro ao carregar as pendências...");

        return of([]);
      })
    );
  }

  public validarCota(itens: { product_id: number; cd_tabela_preco: number; qtd: number }[]): Observable<CotaPromocaoResultado[]> {
    this.headers.set("Content-Type", "application/json");
    let data = {
      "guidclientenapis": localStorage.getItem("guidclientenapis"),
      "itens": itens
    };
    return this.http.post<CotaPromocaoResultado[]>(this.baseUrl + "api/Pedido/ValidarCota", data, { headers: this.headers }).pipe(
      catchError(() => {
        this.toastr.error("", "Houve um erro ao validar a cota, contate o suporte...");
        return of([] as CotaPromocaoResultado[]);
      })
    );
  }

  public baixarCotaPromocao(itens: { product_id: number; cd_tabela_preco: number; qtd: number }[]): Observable<CotaPromocaoResultado[]> {
    this.headers.set("Content-Type", "application/json");
    let data = {
      "guidclientenapis": localStorage.getItem("guidclientenapis"),
      "itens": itens
    };
    return this.http.post<CotaPromocaoResultado[]>(this.baseUrl + "api/Pedido/BaixarCotaPromocao", data, { headers: this.headers }).pipe(
      catchError(() => {
        this.toastr.error("", "Houve um erro ao baixar a cota, contate o suporte...");
        return of([] as CotaPromocaoResultado[]);
      })
    );
  }
}
